import React, { useMemo } from 'react';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SelectDropdown,
  SelectDropdownOption,
} from '@/components/common/SelectDropdown';

/** Thousands-separated display for counts and page numbers (64958 → 64,958). */
const fmt = (n: number) => n.toLocaleString();

export interface PaginationState {
  pageSize: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalItems?: number;
  /**
   * A known lower bound on the total when the exact total is unknown — a
   * counting walk that gave up at a cap or a time budget. Rendered as "of N+"
   * in the items line only; deliberately NOT `totalItems`, which would let a
   * floor masquerade as a page count in the page-number controls.
   */
  totalItemsFloor?: number;
  startItem?: number;
  endItem?: number;
}

export interface TablePaginationProps {
  pagination: PaginationState;
  onPageSizeChange: (pageSize: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onFirstPage?: () => void;
  onLastPage?: () => void;
  onGoToPage?: (page: number) => void;
  showRowsPerPage?: boolean;
  showPageInfo?: boolean;
  showPageNumbers?: boolean;
  rowsPerPageOptions?: number[];
}

// Left-side item count line. Three shapes: full range + total; range with the
// total still unknown (e.g. an async count that hasn't landed yet); bare page
// number when even the range is unknown (cursor-style pagination).
function PaginationItemsInfo({
  totalItems,
  totalItemsFloor,
  startItem,
  endItem,
  currentPage,
  hasNextPage,
}: {
  totalItems?: number;
  totalItemsFloor?: number;
  startItem?: number;
  endItem?: number;
  currentPage: number;
  hasNextPage: boolean;
}) {
  if (startItem !== undefined && endItem !== undefined) {
    if (totalItems !== undefined) {
      return (
        <T context="pagination showing item range">
          Showing <Var>{fmt(startItem)}</Var> to <Var>{fmt(endItem)}</Var> of{' '}
          <Var>{fmt(totalItems)}</Var> results
        </T>
      );
    }
    return totalItemsFloor !== undefined ? (
      <T context="pagination showing item range when only a lower bound on the total is known">
        Showing <Var>{fmt(startItem)}</Var> to <Var>{fmt(endItem)}</Var> of{' '}
        <Var>{fmt(totalItemsFloor)}</Var>+ results
      </T>
    ) : (
      <T context="pagination showing item range while the total is unknown">
        Showing <Var>{fmt(startItem)}</Var> to <Var>{fmt(endItem)}</Var> of many
        results
      </T>
    );
  }
  if (totalItems !== undefined) return null;
  return (
    <>
      <T context="pagination current page without total">
        Page <Var>{fmt(currentPage)}</Var>
      </T>
      {hasNextPage && (
        <T context="pagination indicator that there are more pages"> of many</T>
      )}
    </>
  );
}

// Windowed page-number buttons (up to 5) with first/last shortcuts and
// ellipses — offset pagination with a known total only.
function PageNumberButtons({
  currentPage,
  totalPages,
  onGoToPage,
}: {
  currentPage: number;
  totalPages: number;
  onGoToPage: (page: number) => void;
}) {
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  // Adjust start if we're near the end
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center space-x-1">
      {startPage > 1 && (
        <>
          <Button
            variant={1 === currentPage ? 'default' : 'outline'}
            size="icon"
            className="size-8 text-xs"
            onClick={() => onGoToPage(1)}
          >
            1
          </Button>
          {startPage > 2 && <span className="text-muted-foreground">...</span>}
        </>
      )}
      {pages.map((page) => (
        <Button
          key={page}
          variant={page === currentPage ? 'default' : 'outline'}
          size="icon"
          className="size-8 text-xs"
          onClick={() => onGoToPage(page)}
        >
          {fmt(page)}
        </Button>
      ))}
      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && (
            <span className="text-muted-foreground">...</span>
          )}
          <Button
            variant={totalPages === currentPage ? 'default' : 'outline'}
            size="icon"
            className="size-8 text-xs"
            onClick={() => onGoToPage(totalPages)}
          >
            {fmt(totalPages)}
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * Reusable pagination component using shadcn/ui components.
 * Works with both cursor-based and offset-based pagination.
 */
export const TablePagination: React.FC<TablePaginationProps> = ({
  pagination,
  onPageSizeChange,
  onPreviousPage,
  onNextPage,
  onFirstPage,
  onLastPage,
  onGoToPage,
  showRowsPerPage = true,
  showPageInfo = true,
  showPageNumbers = false,
  rowsPerPageOptions = [10, 20, 25, 30, 40, 50, 100],
}) => {
  const gt = useGT();
  const {
    pageSize,
    currentPage,
    hasNextPage,
    hasPreviousPage,
    totalItems,
    totalItemsFloor,
    startItem,
    endItem,
  } = pagination;

  // Generate page options for dropdown when we have totalItems and onGoToPage
  const pageOptions = useMemo<SelectDropdownOption[]>(() => {
    if (totalItems === undefined || !onGoToPage) {
      return [];
    }
    const totalPages = Math.ceil(totalItems / pageSize);
    return Array.from({ length: totalPages }, (_, i) => {
      const page = i + 1;
      return {
        id: `page-${page}`,
        label: fmt(page), // Just the page number in dropdown
        display: `Page ${fmt(page)} of ${fmt(totalPages)}`, // Full text when collapsed
        value: page,
      };
    });
  }, [totalItems, pageSize, onGoToPage]);

  // Current page option for SelectDropdown
  const currentPageOption = useMemo<SelectDropdownOption | null>(() => {
    if (totalItems === undefined || !onGoToPage) {
      return null;
    }
    return pageOptions.find((opt) => opt.value === currentPage) || null;
  }, [pageOptions, currentPage, totalItems, onGoToPage]);

  return (
    <div className="flex items-center justify-between px-2 pt-4">
      {/* Left side - Item count info */}
      <div className="flex-1 text-sm text-muted-foreground">
        {showPageInfo && (
          <PaginationItemsInfo
            totalItems={totalItems}
            totalItemsFloor={totalItemsFloor}
            startItem={startItem}
            endItem={endItem}
            currentPage={currentPage}
            hasNextPage={hasNextPage}
          />
        )}
      </div>

      {/* Right side - Controls */}
      <div className="flex items-center space-x-6 lg:space-x-8">
        {/* Rows per page selector */}
        {showRowsPerPage && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">
              <T context="pagination rows per page label">Rows per page</T>
            </p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[80px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {rowsPerPageOptions.map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Page info */}
        {showPageInfo && (
          <>
            {onGoToPage &&
            totalItems !== undefined &&
            pageOptions.length > 0 ? (
              <SelectDropdown
                options={pageOptions}
                value={currentPageOption}
                onChange={(selected) => {
                  if (selected?.value && typeof selected.value === 'number') {
                    onGoToPage(selected.value);
                  }
                }}
                placeholder={gt('Select page', {
                  context: 'pagination select page placeholder',
                })}
                fullWidth={false}
                size="default"
                className="w-[140px]"
              />
            ) : (
              <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                {totalItems !== undefined ? (
                  <T context="pagination current page of total pages">
                    Page <Var>{fmt(currentPage)}</Var> of{' '}
                    <Var>
                      {fmt(Math.max(1, Math.ceil(totalItems / pageSize)))}
                    </Var>
                  </T>
                ) : (
                  <T context="pagination current page number">
                    Page <Var>{fmt(currentPage)}</Var>
                  </T>
                )}
              </div>
            )}
          </>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center space-x-2">
          {/* First page button */}
          {onFirstPage && (
            <Button
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={onFirstPage}
              disabled={!hasPreviousPage}
            >
              <span className="sr-only">
                <T context="pagination go to first page button label">
                  Go to first page
                </T>
              </span>
              <CaretDoubleLeftIcon className="h-4 w-4" />
            </Button>
          )}

          {/* Previous page button */}
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={onPreviousPage}
            disabled={!hasPreviousPage}
          >
            <span className="sr-only">
              <T context="pagination go to previous page button label">
                Go to previous page
              </T>
            </span>
            <CaretLeftIcon className="h-4 w-4" />
          </Button>

          {/* Page number buttons */}
          {showPageNumbers && onGoToPage && totalItems !== undefined && (
            <PageNumberButtons
              currentPage={currentPage}
              totalPages={Math.ceil(totalItems / pageSize)}
              onGoToPage={onGoToPage}
            />
          )}

          {/* Next page button */}
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={onNextPage}
            disabled={!hasNextPage}
          >
            <span className="sr-only">
              <T context="pagination go to next page button label">
                Go to next page
              </T>
            </span>
            <CaretRightIcon className="h-4 w-4" />
          </Button>

          {/* Last page button */}
          {onLastPage && (
            <Button
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={onLastPage}
              disabled={!hasNextPage}
            >
              <span className="sr-only">
                <T context="pagination go to last page button label">
                  Go to last page
                </T>
              </span>
              <CaretDoubleRightIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
